import { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import ResultBoxSection from '@App/components/search/ResultBoxSection';
import ResultBoxTag from '@App/components/search/ResultBoxTag';
import ResultBoxLink from '@App/components/search/ResultBoxLink';
import { ALBUM, PLAYLIST } from '@App/routes';
import { SoundEffectData } from '@App/components/model/SoundEffectPropType';

interface TagItem {
  id: string
  name: string
}

interface TrackItem {
  reference: string
  title: string
  album: {
    reference: string
    coverAsThumbnail: string
  }
  nbVersions: number
  matchedVersions: string[]
}

interface AlbumItem {
  reference: string
  title: string
  coverAsThumbnail: string
  nbTracks: number
}

interface PlaylistItem {
  slug: string
  id: string
  title: string
  coverAsThumbnail: string
  nbTracks: number
}

interface Section<T> {
  count: number
  list: T[]
}

export interface ResultBoxProps {
  onSearch: () => void
  onFind: () => void
  onSubmit: () => void
  tags?: Section<TagItem> | null
  tracks?: Section<TrackItem> | null
  albums?: Section<AlbumItem> | null
  playlists?: Section<PlaylistItem> | null
  soundEffects?: Section<SoundEffectData> | null
  onSoundEffects?: (() => void) | null
  loading?: boolean
}

export default class ResultBox extends Component<ResultBoxProps> {
  static propTypes = {
    onSearch: PropTypes.func.isRequired,
    onFind: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    tags: PropTypes.object,
    tracks: PropTypes.object,
    albums: PropTypes.object,
    playlists: PropTypes.object,
    soundEffects: PropTypes.object,
    onSoundEffects: PropTypes.func,
    loading: PropTypes.bool,
  };

  static defaultProps = {
    tags: null,
    tracks: null,
    albums: null,
    playlists: null,
    soundEffects: null,
    onSoundEffects: null,
    loading: true,
  };

  static renderTag(tag: TagItem, onUse: () => void) {
    const { id, name } = tag;

    return <ResultBoxTag key={id} id={id} title={name} onUse={onUse} />;
  }

  static renderTrack(track: TrackItem, onUse: () => void) {
    const { reference, title, album, nbVersions, matchedVersions } = track;

    const subtitle = (
      <div className="media__info__subtitle">
        <span>{matchedVersions.length ? matchedVersions.join(', ') : reference}</span>
        <span>{I18n.t('model.track.versions', { count: nbVersions })}</span>
      </div>
    );

    return <ResultBoxLink
      key={reference}
      route={ALBUM.name}
      parameters={{ reference: album.reference }}
      anchor={reference}
      title={title}
      cover={album.coverAsThumbnail}
      subtitle={subtitle}
      onUse={onUse}
    />;
  }

  static renderAlbum(album: AlbumItem, onUse: () => void) {
    const { reference, title, coverAsThumbnail, nbTracks } = album;

    return <ResultBoxLink
      key={reference}
      route={ALBUM.name}
      parameters={{ reference }}
      title={title}
      cover={coverAsThumbnail}
      subtitle={`${reference} - ${I18n.t('model.track.countTracks', { count: nbTracks })}`}
      onUse={onUse}
    />;
  }

  static renderPlaylist(playlist: PlaylistItem, onUse: () => void) {
    const { slug, id, title, coverAsThumbnail, nbTracks } = playlist;

    return <ResultBoxLink
      key={slug}
      route={PLAYLIST.name}
      parameters={{ slug, id }}
      title={title}
      cover={coverAsThumbnail}
      subtitle={I18n.t('model.track.countTracks', { count: nbTracks })}
      onUse={onUse}
    />;
  }

  static renderSoundEffect(sound: SoundEffectData, onUse: () => void) {
    const { id, title, category, artworkUrl } = sound;

    // No per-item route: the whole section redirects to /effets-sonores (see Search.onSoundEffects).
    // The artwork is shown like a music entry's cover, falling back to the generic icon for a sound
    // whose library ships none.
    return (
      <button key={id} type="button" className="media" onClick={onUse}>
        {artworkUrl
          ? <span className="media__cover" style={{ backgroundImage: `url('${artworkUrl}')` }} />
          : <span className="media__cover media__cover--icon">
            <i className="icon icon--control-play" aria-hidden="true" />
          </span>
        }
        <span className="media__info">
          <strong>{title}</strong>
          {category ? <div className="media__info__subtitle"><span>{category}</span></div> : null}
        </span>
      </button>
    );
  }

  renderSoundEffectsSection() {
    const { soundEffects, onSoundEffects } = this.props;

    if (!soundEffects || soundEffects.count < 1) {
      return null;
    }

    return (
      <ResultBoxSection label={I18n.t('search.results.soundEffects')} count={soundEffects.count} onSeeAll={onSoundEffects}>
        {soundEffects.list.map(sound => (this.constructor as typeof ResultBox).renderSoundEffect(sound, onSoundEffects!))}
      </ResultBoxSection>
    );
  }

  renderSection<T>(
    search: Section<T>,
    label: string,
    renderChild: (item: T, onUse: () => void) => JSX.Element,
    onUse: () => void,
    onSeeAll: (() => void) | null = null,
  ) {
    const { count, list } = search;

    if (count < 1) {
      return null;
    }

    return (
      <ResultBoxSection label={label} count={count} onSeeAll={onSeeAll}>
        {list.map(item => renderChild(item, onUse))}
      </ResultBoxSection>
    );
  }

  render() {
    const { loading, tags, tracks, albums, playlists, soundEffects, onSearch, onFind, onSubmit } = this.props;
    const { renderTag, renderTrack, renderAlbum, renderPlaylist } = this.constructor as typeof ResultBox;

    if (loading) {
      return <div className="result-box loading"></div>;
    }

    const tagsSection = this.renderSection(tags!, I18n.t('search.results.tags'), renderTag, onSearch);
    const tracksSection = this.renderSection(tracks!, I18n.t('search.results.tracks'), renderTrack, onFind, onSubmit);
    const albumsSection = this.renderSection(albums!, I18n.t('search.results.albums'), renderAlbum, onFind);
    const playlistsSection = this.renderSection(playlists!, I18n.t('search.results.playlists'), renderPlaylist, onFind);
    const soundEffectsSection = this.renderSoundEffectsSection();

    if (!tagsSection && !tracksSection && !albumsSection && !playlistsSection && !soundEffectsSection) {
      // The sound-effects query resolves independently and can still be in flight after the music
      // query settled (`renderSoundEffectsSection` returns null while count is 0, including during
      // loading). Keep the loading box up rather than flashing a premature "no results" that would
      // then be replaced once the section arrives.
      if ((soundEffects as { loading?: boolean } | null)?.loading) {
        return <div className="result-box loading"></div>;
      }

      return (
        <div className="result-box result-box--empty">
          <h3 className="message">{I18n.t('search.results.no-match')}</h3>
        </div>
      );
    }

    return (
      <div className="result-box">
        {tagsSection}
        {tracksSection}
        {albumsSection}
        {playlistsSection}
        {soundEffectsSection}
      </div>
    );
  }
}
